import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { toolCategories } from '../../data/catalog';
import { SectionHeading, DISPLAY, MONO, fadeUp } from './shared';

const catAccent = { 'one-pagers': '#a78bfa', 'training-competency': '#34d399', 'quality-improvement': '#60a5fa', 'visual-management': '#fbbf24', communication: '#f472b6' };
const tabId = { 'one-pagers': 'one-pagers', 'training-competency': 'competency', 'quality-improvement': 'qi', 'visual-management': 'signage', communication: 'admin-ai' };

function ToolCard({ t, accent }) {
  return (
    <div className="shrink-0 w-[260px] rounded-2xl border border-white/10 bg-[#0f172a]/80 backdrop-blur-xl p-5 hover:border-cyan-500/40 hover:-translate-y-1 transition-all duration-300" data-testid={`tool-card-${t.id}`}>
      <div className="flex items-center justify-between">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}><t.icon className="w-5 h-5" /></div>
        {t.featured && <span className="text-[9px] uppercase tracking-[0.18em] text-cyan-300 px-2 py-0.5 rounded-full border border-cyan-500/30" style={{ fontFamily: MONO }}>Flagship</span>}
      </div>
      <div className="mt-4 font-medium text-slate-100" style={{ fontFamily: DISPLAY }}>{t.name}</div>
      <div className="mt-1 text-sm text-slate-400 leading-relaxed line-clamp-2">{t.description}</div>
    </div>
  );
}

export function ToolCarousel() {
  const [cat, setCat] = useState('all');
  const tools = useMemo(() => {
    const cats = cat === 'all' ? toolCategories : toolCategories.filter((c) => c.id === cat);
    return cats.flatMap((c) => c.tools.map((t) => ({ ...t, accent: catAccent[c.id] || '#67e8f9' })));
  }, [cat]);
  const total = toolCategories.reduce((n, c) => n + c.tools.length, 0);
  const loop = [...tools, ...tools];
  const duration = Math.max(30, tools.length * 4);

  return (
    <section id="tools" className="relative py-20 px-6 lg:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp}><SectionHeading eyebrow={`${total} tools · 5 categories`} title="The complete ecosystem — from huddle sheet to RCA." lead="Every tool shares one library, one brand and one AI. Drag or hover to pause; pick a category to focus." /></motion.div>
        <div className="mt-8 flex flex-wrap gap-2">
          <button onClick={() => setCat('all')} className={`text-xs px-4 py-2 rounded-full border transition-all ${cat === 'all' ? 'border-cyan-400/60 bg-cyan-500/15 text-cyan-200' : 'border-white/10 text-slate-400 hover:text-slate-200'}`} style={{ fontFamily: MONO }} data-testid="tool-category-tab-all">All tools</button>
          {toolCategories.map((c) => <button key={c.id} onClick={() => setCat(c.id)} className={`text-xs px-4 py-2 rounded-full border transition-all ${cat === c.id ? 'border-cyan-400/60 bg-cyan-500/15 text-cyan-200' : 'border-white/10 text-slate-400 hover:text-slate-200'}`} style={{ fontFamily: MONO }} data-testid={`tool-category-tab-${tabId[c.id]}`}>{c.name}</button>)}
        </div>
      </div>
      <div className="relative mt-10 group" data-testid="tool-marquee">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 z-10" style={{ background: 'linear-gradient(90deg,#080c14,transparent)' }} />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 z-10" style={{ background: 'linear-gradient(270deg,#080c14,transparent)' }} />
        <div key={cat} className="flex gap-4 w-max px-6 landing-marquee group-hover:[animation-play-state:paused]" style={{ animationDuration: `${duration}s` }}>
          {loop.map((t, i) => <ToolCard key={`${t.id}-${i}`} t={t} accent={t.accent} />)}
        </div>
      </div>
      <style>{`@keyframes landing-marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}} .landing-marquee{animation-name:landing-marquee;animation-timing-function:linear;animation-iteration-count:infinite;}`}</style>
    </section>
  );
}
